import React, { useMemo } from "react";
import PartyCallMonthCalendarSheet from "./PartyCallMonthCalendarSheet";
import PartyCallYearlyIndexSheet from "./PartyCallYearlyIndexSheet";
import { computeAllMonthsCalendarData } from "./partyCallCalendarHelpers";
import "./PartyCallPrintReport.css";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

const PartyCallYearlyCalendarReport = ({
  party,
  year,
  projectId = "ALL",
  partyProjects = [],
  filteredCallsByProject = [],
  onlyMonthsWithCalls = true,
  isScreenPreview = false,
}) => {
  const selectedProjectName = useMemo(() => {
    if (projectId === "ALL") return null;
    if (projectId === "NO_PROJECT") return "General";
    if (Array.isArray(partyProjects)) {
      const found = partyProjects.find((p) => String(p?.id) === String(projectId));
      if (found?.name) return found.name;
    }
    return projectId;
  }, [projectId, partyProjects]);

  const nowFormatted = useMemo(() => {
    const d = new Date();
    return `${formatDate(d)} at ${formatTime(d)}`;
  }, []);

  // Compute all 12 months calendar & summary data
  const allMonths = useMemo(() => {
    return computeAllMonthsCalendarData(filteredCallsByProject || [], year);
  }, [filteredCallsByProject, year]);

  // Filter based on user preference: default only months with calls (no empty months)
  const displayMonths = useMemo(() => {
    if (onlyMonthsWithCalls) {
      return (allMonths || []).filter((m) => m.hasCalls);
    }
    return allMonths || [];
  }, [allMonths, onlyMonthsWithCalls]);

  // Total pages = 1 (Page 1 Master Index Sheet) + displayMonths.length (Monthly Calendars)
  const totalPages = useMemo(() => {
    return (displayMonths ? displayMonths.length : 0) + 1;
  }, [displayMonths]);

  // Smooth scroll to target month page in screen preview
  const handleNavigateToPage = (targetPageNum) => {
    const el = document.getElementById(`calendar-sheet-page-${targetPageNum}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div
      id="print-report-root"
      className={`print-report-container ${
        isScreenPreview ? "screen-preview-container" : ""
      }`}
    >
      {/* Page 1: Annual Master Index & Directory Table */}
      <PartyCallYearlyIndexSheet
        party={party}
        year={year}
        selectedProjectName={selectedProjectName}
        activeMonths={displayMonths}
        pageNumber={1}
        totalPages={totalPages}
        isLastPage={displayMonths.length === 0}
        isScreenPreview={isScreenPreview}
        nowFormatted={nowFormatted}
        onNavigateToPage={handleNavigateToPage}
      />

      {/* Pages 2 to totalPages: Active Month Calendar Sheets */}
      {(displayMonths || []).map((monthData, idx) => (
        <PartyCallMonthCalendarSheet
          key={monthData.month}
          id={`calendar-sheet-page-${idx + 2}`}
          party={party}
          month={monthData.month}
          year={year}
          selectedProjectName={selectedProjectName}
          calendarWeeks={monthData.calendarWeeks || []}
          monthStats={
            monthData.monthStats || {
              total: 0,
              completed: 0,
              pending: 0,
              other: 0,
            }
          }
          pageNumber={idx + 2}
          totalPages={totalPages}
          isLastPage={idx === displayMonths.length - 1}
          isScreenPreview={isScreenPreview}
          nowFormatted={nowFormatted}
        />
      ))}
    </div>
  );
};

export default PartyCallYearlyCalendarReport;
